"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Bot, Loader2, Play, RotateCcw, Square } from "lucide-react";
import type { Issue, TaskExecution, User } from "@/lib/types";
import { formatRelative } from "@/lib/utils";

type ExecutionRow = {
  id: string;
  workspace_id: string;
  issue_id: string;
  agent_user_id: string;
  requested_by: string | null;
  agent_run_id: string | null;
  status: TaskExecution["status"];
  attempt: number;
  input: Record<string, unknown>;
  output_summary: string;
  error: string | null;
  started_at: string | null;
  ended_at: string | null;
  cancel_requested_at: string | null;
  heartbeat_at: string | null;
  created_at: string;
  updated_at: string;
};

function mapExecution(row: ExecutionRow): TaskExecution {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    issueId: row.issue_id,
    agentUserId: row.agent_user_id,
    requestedBy: row.requested_by,
    agentRunId: row.agent_run_id,
    status: row.status,
    attempt: row.attempt,
    input: row.input,
    outputSummary: row.output_summary,
    error: row.error,
    startedAt: row.started_at ? Date.parse(row.started_at) : null,
    endedAt: row.ended_at ? Date.parse(row.ended_at) : null,
    cancelRequestedAt: row.cancel_requested_at
      ? Date.parse(row.cancel_requested_at)
      : null,
    heartbeatAt: row.heartbeat_at ? Date.parse(row.heartbeat_at) : null,
    createdAt: Date.parse(row.created_at),
    updatedAt: Date.parse(row.updated_at),
  };
}

export function TaskAgentExecution({
  issue,
  agent,
  canWrite,
}: {
  issue: Issue;
  agent: User | undefined;
  canWrite: boolean;
}) {
  const [executions, setExecutions] = useState<TaskExecution[]>([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!issue.id || issue.id.startsWith("iss_")) return;
    setLoading(true);
    try {
      const response = await fetch(
        `/api/workspaces/${issue.workspaceId}/tasks/${issue.id}/executions`,
        { cache: "no-store" }
      );
      if (!response.ok) return;
      const payload = (await response.json()) as { executions?: ExecutionRow[] };
      setExecutions((payload.executions ?? []).map(mapExecution));
    } finally {
      setLoading(false);
    }
  }, [issue.id, issue.workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const latest = executions[0];
  const active = useMemo(
    () =>
      executions.find((execution) =>
        ["queued", "running", "waiting"].includes(execution.status)
      ),
    [executions]
  );

  useEffect(() => {
    if (!active) return;
    const interval = window.setInterval(() => {
      void load();
    }, 2_500);
    return () => window.clearInterval(interval);
  }, [active, load]);

  async function start(retryOf?: string) {
    if (!agent) return;
    setActing(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/workspaces/${issue.workspaceId}/tasks/${issue.id}/executions`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            agent_user_id: agent.id,
            input: `Work on ${issue.identifier}: ${issue.title}`,
            ...(retryOf ? { retry_of: retryOf } : {}),
          }),
        }
      );
      const payload = (await response.json().catch(() => ({}))) as {
        error?: string;
      };
      if (!response.ok) throw new Error(payload.error ?? "execution_failed");
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "execution_failed");
    } finally {
      setActing(false);
    }
  }

  async function cancel() {
    if (!active) return;
    setActing(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/workspaces/${issue.workspaceId}/executions/${active.id}`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ action: "cancel" }),
        }
      );
      if (!response.ok) throw new Error("cancel_failed");
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "cancel_failed");
    } finally {
      setActing(false);
    }
  }

  if (!agent && executions.length === 0) return null;

  return (
    <section className="rounded-md border border-[var(--border)] bg-[var(--bg)] p-3">
      <div className="flex items-center gap-2">
        <Bot size={12} className="text-[var(--brand-indigo)]" />
        <span className="text-[11px] font-medium text-[var(--fg-secondary)]">
          Agent execution
        </span>
        {loading && <Loader2 size={11} className="ml-auto animate-spin text-[var(--fg-muted)]" />}
      </div>

      {latest ? (
        <div className="mt-2">
          <div className="flex items-center gap-2">
            <ExecutionStatus status={latest.status} />
            <span className="text-[10.5px] text-[var(--fg-muted)]">
              Attempt {latest.attempt} · {formatRelative(latest.updatedAt)}
            </span>
          </div>
          {(latest.outputSummary || latest.error) && (
            <p className="mt-2 line-clamp-3 text-[11.5px] leading-relaxed text-[var(--fg-tertiary)]">
              {latest.outputSummary || latest.error}
            </p>
          )}
        </div>
      ) : (
        <p className="mt-2 text-[11.5px] text-[var(--fg-muted)]">
          {agent ? "Ready to delegate this task." : "No agent has run this task."}
        </p>
      )}

      {error && <p className="mt-2 text-[10.5px] text-[var(--red)]">{error}</p>}

      {canWrite && agent && (
        <div className="mt-3 flex items-center gap-2">
          {active ? (
            <button
              type="button"
              onClick={cancel}
              disabled={acting || active.cancelRequestedAt !== null}
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-2 py-1 text-[11px] font-medium text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-50 transition-colors"
            >
              <Square size={10} />
              {active.cancelRequestedAt ? "Stopping…" : "Cancel"}
            </button>
          ) : latest ? (
            <button
              type="button"
              onClick={() => start(latest.id)}
              disabled={acting}
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-2 py-1 text-[11px] font-medium text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-50 transition-colors"
            >
              <RotateCcw size={10} />
              Retry
            </button>
          ) : (
            <button
              type="button"
              onClick={() => start()}
              disabled={acting}
              className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-2 py-1 text-[11px] font-medium text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 transition-colors"
            >
              <Play size={10} />
              Run agent
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function ExecutionStatus({ status }: { status: TaskExecution["status"] }) {
  const color =
    status === "completed"
      ? "var(--green)"
      : status === "failed"
        ? "var(--red)"
        : status === "canceled"
          ? "var(--fg-muted)"
          : "var(--yellow)";
  return (
    <span
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium capitalize"
      style={{
        color,
        background: `color-mix(in srgb, ${color} 12%, transparent)`,
      }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      {status}
    </span>
  );
}
